import { useCallback } from "react";

import { GetCurrentNoticesRequest, NoticePageContext } from "src/api/Tmd.dtos";
import type { ILayoutItemContext } from "src/contexts/layouts/LayoutContext";
import useApi from "src/hooks/api/useApi";

/**
 * Loads current notices with a 60-second SWR refresh interval. A request is supplied even without
 * page IDs; context is included when any ID is not undefined, including zero/null.
 * The shared API lifecycle controls polling; this is not an exact wall-clock timer.
 *
 * Preconditions: A service/API provider supplies the client; the endpoint owns access checks.
 */
const useCurrentNotices = (pageContext: ILayoutItemContext) => {
    const { groupId, trialId, institutionId, trialInstitutionId, patientId, personnelId } =
        pageContext;
    const hasPageContext =
        groupId !== undefined ||
        trialId !== undefined ||
        institutionId !== undefined ||
        trialInstitutionId !== undefined ||
        patientId !== undefined ||
        personnelId !== undefined;
    const request = useCallback(
        () =>
            new GetCurrentNoticesRequest({
                pageContext: hasPageContext
                    ? new NoticePageContext({
                          groupId,
                          trialId,
                          institutionId,
                          trialInstitutionId,
                          patientId,
                          personnelId
                      })
                    : undefined
            }),
        [
            groupId,
            trialId,
            institutionId,
            trialInstitutionId,
            patientId,
            personnelId,
            hasPageContext
        ]
    );
    return useApi(
        "post",
        request,
        {
            getData: response => response?.data?.notices,
            getError: response => (response?.error ?? response?.data)?.responseStatus,
            getAdditionalState: () => ({})
        },
        {
            refreshInterval: 60_000
        }
    );
};
export default useCurrentNotices;
